import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { X, Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { fetchServices, fetchStaff, fetchClients, createBooking } from '../../lib/bookingsApi';
import { useAuth } from '../../context/AuthContext';

const schema = z.object({
  serviceId:   z.string().uuid('Required'),
  staffId:     z.string().uuid('Required'),
  date:        z.string().min(1, 'Required'),
  slotIso:     z.string().min(1, 'Pick a time slot'),
  clientName:  z.string().min(1, 'Required'),
  clientEmail: z.string().email('Invalid email').or(z.literal('')).optional(),
  clientPhone: z.string().optional(),
  notes:       z.string().optional(),
});

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function toKey(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function SlotPicker({ slug, serviceId, staffId, date, value, onChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!serviceId || !staffId || !date) { setSlots([]); return; }
    setLoading(true);
    const q = new URLSearchParams({ serviceId, staffId, date });
    axios.get(`${BASE}/api/public/${slug}/availability?${q}`)
      .then(r => {
        const groups = r.data.data || [];
        const all = groups.flatMap(g => (g.slots || []).map(s => ({ iso: s, staffId: g.staffId })));
        setSlots(all);
      })
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [slug, serviceId, staffId, date]);

  if (!serviceId || !staffId || !date) return null;

  if (loading) return <p className="text-xs text-ink-muted">Loading slots…</p>;
  if (!slots.length) return <p className="text-xs text-ink-muted">No available slots for this date.</p>;

  return (
    <div className="grid grid-cols-4 gap-2">
      {slots.map(({ iso }) => {
        const t = new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        return (
          <button
            key={iso}
            type="button"
            onClick={() => onChange(iso)}
            className={`text-xs py-2 rounded-lg border font-medium transition-all
              ${value === iso
                ? 'bg-primary text-white border-primary'
                : 'border-surface-border text-ink hover:border-primary hover:bg-primary/5'}`}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}

function ClientSearch({ value, onChange }) {
  const [q, setQ] = useState(value || '');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ['clients-search', q],
    queryFn: () => fetchClients({ search: q, limit: 8 }),
    enabled: q.length > 1,
  });

  const clients = data?.data || [];

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <input
        className="input w-full"
        placeholder="Search or type new client name"
        value={q}
        onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && clients.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-surface-border rounded-lg shadow-md py-1 max-h-48 overflow-y-auto">
          {clients.map(c => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-surface-50 text-ink"
              onClick={() => { setQ(c.name); onChange(c.name, c); setOpen(false); }}
            >
              <span className="font-medium">{c.name}</span>
              {c.email && <span className="text-ink-muted ml-2 text-xs">{c.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NewBookingModal({ onClose }) {
  const { tenant } = useAuth();
  const qc = useQueryClient();

  const { data: services = [] } = useQuery({ queryKey: ['services'], queryFn: fetchServices });
  const { data: staff = [] }    = useQuery({ queryKey: ['staff'],    queryFn: fetchStaff });

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { serviceId: '', staffId: '', date: toKey(new Date()), slotIso: '', clientName: '', clientEmail: '', clientPhone: '', notes: '' },
  });

  const [serviceId, staffId, date, slotIso] = watch(['serviceId', 'staffId', 'date', 'slotIso']);

  const mutation = useMutation({
    mutationFn: (data) => createBooking({
      serviceId:   data.serviceId,
      staffId:     data.staffId,
      startAt:     data.slotIso,
      clientName:  data.clientName,
      clientEmail: data.clientEmail || null,
      clientPhone: data.clientPhone || null,
      notes:       data.notes || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg my-4">

        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <h2 className="text-sm font-semibold text-ink">New Booking</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-muted transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="px-5 py-4 space-y-4">

          {mutation.isError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {mutation.error?.response?.data?.error?.message || 'Failed to create booking.'}
            </div>
          )}

          {/* Service */}
          <div>
            <label className="label">Service *</label>
            <select {...register('serviceId')} className="input w-full">
              <option value="">Select service…</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>
              ))}
            </select>
            {errors.serviceId && <p className="field-error">{errors.serviceId.message}</p>}
          </div>

          {/* Staff */}
          <div>
            <label className="label">Staff *</label>
            <select {...register('staffId')} className="input w-full">
              <option value="">Select staff…</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {errors.staffId && <p className="field-error">{errors.staffId.message}</p>}
          </div>

          {/* Date */}
          <div>
            <label className="label">Date *</label>
            <input type="date" {...register('date')} className="input w-full"
              min={toKey(new Date())} />
            {errors.date && <p className="field-error">{errors.date.message}</p>}
          </div>

          {/* Time slots */}
          <div>
            <label className="label">Time *</label>
            <Controller
              name="slotIso"
              control={control}
              render={({ field }) => (
                <SlotPicker
                  slug={tenant?.slug}
                  serviceId={serviceId}
                  staffId={staffId}
                  date={date}
                  value={field.value}
                  onChange={(iso) => { field.onChange(iso); setValue('slotIso', iso); }}
                />
              )}
            />
            {errors.slotIso && <p className="field-error">{errors.slotIso.message}</p>}
            {slotIso && (
              <p className="text-xs text-primary mt-1">
                Selected: {new Date(slotIso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
          </div>

          <hr className="border-surface-border" />

          {/* Client */}
          <div>
            <label className="label">Client name *</label>
            <Controller
              name="clientName"
              control={control}
              render={({ field }) => (
                <ClientSearch
                  value={field.value}
                  onChange={(name, client) => {
                    field.onChange(name);
                    if (client) {
                      if (client.email) setValue('clientEmail', client.email);
                      if (client.phone) setValue('clientPhone', client.phone);
                    }
                  }}
                />
              )}
            />
            {errors.clientName && <p className="field-error">{errors.clientName.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input type="email" {...register('clientEmail')} className="input w-full" placeholder="jane@example.com" />
              {errors.clientEmail && <p className="field-error">{errors.clientEmail.message}</p>}
            </div>
            <div>
              <label className="label">Phone</label>
              <input {...register('clientPhone')} className="input w-full" placeholder="+1 555 000 0000" />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea {...register('notes')} rows={2} className="input w-full resize-none" placeholder="Any notes…" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? 'Creating…' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
